import React, { useState, useEffect, useCallback, useMemo, useRef, memo, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  AlertTriangle, 
  RotateCcw, 
  Target,
  Sparkles,
  Music2
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useUniversalTextSizing, getUniversalTextConfig } from '../utils/cardTitleSizing';
import { CardTitle, BodyText, SmallText, CaptionText, UniversalButton, useUniversalSizing } from './UniversalText';
import { useContainerSize } from '../hooks/useContainerSize';

// Type definitions
export interface StyleScoreSheet {
  styleId: string;
  styleName: string;
  styleSpecificData?: Record<string, Record<string, string[]>>; // NEW: categoryGroups -> categories -> criteria
  categories?: Record<string, string[]>; // OLD: for backward compatibility
}

export interface FeedbackData {
  [key: string]: 'good' | 'needs-work';
}

export interface CriteriaItemProps {
  categoryName: string;
  index: number;
  criteriaText: string;
  feedback?: 'good' | 'needs-work';
  isAnimating: boolean;
  onFeedbackChange: (categoryName: string, index: number, criteriaText: string, feedbackType: 'good' | 'needs-work') => void;
  universalConfig: any;
}

export interface CategoryColumnProps {
  categoryName: string;
  criteria: string[];
  feedback: FeedbackData;
  animatingItems: Set<string>;
  groupInfo?: { groupName: string, groupIndex: number };
  onFeedbackChange: (categoryName: string, index: number, criteriaText: string, feedbackType: 'good' | 'needs-work') => void;
  universalConfig: any;
}

export interface StyleSpecificScoringCardProps {
  currentRoutine?: any | null;
  styleScoreSheets?: StyleScoreSheet[];
  styleCategories?: string[];
  onFeedbackUpdate?: ((feedback: FeedbackData) => void) | null;
  initialFeedback?: FeedbackData;
  className?: string;
  containerSize?: 'small' | 'medium' | 'large' | 'xlarge';
}

export type ContainerSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface SizeConfig {
  headerSize: string;
  categoryHeaderSize: string;
  criteriaSize: string;
  buttonSize: string;
  iconSize: number;
  spacing: string;
  categorySpacing: string;
  padding: string;
  // Container-specific layout properties
  headerSpacing: string;
  titleSize: string;
  iconSizeClass: string;
  buttonPadding: string;
  buttonTextSize: string;
  columnGap: string;
  itemSpacing: string;
  baseOffset: number;
  maxHeight: string;
  minHeight: string;
}

// Helper function to convert database format back to internal format
const convertDatabaseFeedbackToInternal = (databaseFeedback: any): FeedbackData => {
  if (!databaseFeedback || typeof databaseFeedback !== 'object') {
    return {};
  }

  // Check if this is already in individual format (key-value pairs where values are 'good' or 'needs-work')
  const isIndividualFormat = Object.values(databaseFeedback).some(value => 
    value === 'good' || value === 'needs-work'
  );

  if (isIndividualFormat) {
    return databaseFeedback as FeedbackData;
  }

  const internalFeedback: FeedbackData = {};

  // Handle the DynamoDB format first
  if (databaseFeedback.good && databaseFeedback.good.L && Array.isArray(databaseFeedback.good.L)) {
    databaseFeedback.good.L.forEach((item: any) => {
      if (item && item.M && item.M.category && item.M.category.S && item.M.criteria && item.M.criteria.S) {
        const key = `${item.M.category.S}:${item.M.criteria.S}`;
        internalFeedback[key] = 'good';
      }
    });
  }

  if (databaseFeedback['needs-work'] && databaseFeedback['needs-work'].L && Array.isArray(databaseFeedback['needs-work'].L)) {
    databaseFeedback['needs-work'].L.forEach((item: any) => {
      if (item && item.M && item.M.category && item.M.category.S && item.M.criteria && item.M.criteria.S) {
        const key = `${item.M.category.S}:${item.M.criteria.S}`;
        internalFeedback[key] = 'needs-work';
      }
    });
  }

  // Handle the simplified grouped format (for backward compatibility)
  if (databaseFeedback.good && Array.isArray(databaseFeedback.good)) {
    databaseFeedback.good.forEach((item: any) => {
      if (item && typeof item === 'object' && item.category && item.criteria) {
        // New format with category and criteria objects
        const key = `${item.category}:${item.criteria}`;
        internalFeedback[key] = 'good';
      } else if (typeof item === 'string') {
        // Legacy format - just criteria text
        internalFeedback[item] = 'good';
      }
    });
  }

  if (databaseFeedback['needs-work'] && Array.isArray(databaseFeedback['needs-work'])) {
    databaseFeedback['needs-work'].forEach((item: any) => {
      if (item && typeof item === 'object' && item.category && item.criteria) {
        // New format with category and criteria objects
        const key = `${item.category}:${item.criteria}`;
        internalFeedback[key] = 'needs-work';
      } else if (typeof item === 'string') {
        // Legacy format - just criteria text
        internalFeedback[item] = 'needs-work';
      }
    });
  }

  return internalFeedback;
};

// Helper function to flatten the new nested structure into the old format with visual grouping
const flattenStyleScoreSheet = (styleSheet: StyleScoreSheet): { categories: Record<string, string[]>, groupInfo: Record<string, { groupName: string, groupIndex: number }> } => {
  // If using the old format, return as-is with no group info
  if (styleSheet.categories && !styleSheet.styleSpecificData) {
    return { 
      categories: styleSheet.categories, 
      groupInfo: {} 
    };
  }

  // If using the new format, flatten it with group information
  if (styleSheet.styleSpecificData) {
    const flattened: Record<string, string[]> = {};
    const groupInfo: Record<string, { groupName: string, groupIndex: number }> = {};
    
    // REVERSE the order of GLOBAL category groups (e.g., Technical before Performance)
    const reversedGlobalCategories = Object.entries(styleSheet.styleSpecificData).reverse();
    
    let groupIndex = 0;
    reversedGlobalCategories.forEach(([globalCategoryName, genreSpecificCategories]) => {
      // Check if we have order metadata for this global category
      const orderMetadata = (styleSheet as any).orderMetadata?.[globalCategoryName];
      let categoriesEntries;
      
      if (orderMetadata && Array.isArray(orderMetadata)) {
        // Use the stored order from metadata (user's custom drag-and-drop order)
        categoriesEntries = orderMetadata.map(categoryName => [categoryName, genreSpecificCategories[categoryName]]).filter(([name, criteria]) => criteria !== undefined);
        // Add any categories not in the order metadata (newly added categories)
        Object.entries(genreSpecificCategories).forEach(([categoryName, criteria]) => {
          if (!orderMetadata.includes(categoryName)) {
            categoriesEntries.push([categoryName, criteria]);
          }
        });
      } else {
        // No order metadata, use the stored order as-is (no automatic reversing for genre-specific categories)
        categoriesEntries = Object.entries(genreSpecificCategories);
      }
      
      categoriesEntries.forEach(([genreSpecificCategoryName, criteria]) => {
        // Use just the genre-specific category name as the key (no parent group text)
        flattened[genreSpecificCategoryName] = criteria;
        // Store group information for visual styling (using global category for grouping)
        groupInfo[genreSpecificCategoryName] = {
          groupName: globalCategoryName,
          groupIndex: groupIndex
        };
      });
      groupIndex++;
    });
    
    // Debug logging to verify flattening works
    if (process.env.NODE_ENV === 'development') {
      console.log('StyleSpecificScoringCard: Global categories reversed, genre-specific order from user preferences for', styleSheet.styleId, {
        original: styleSheet.styleSpecificData,
        flattened: flattened,
        groupInfo: groupInfo,
        orderMetadata: (styleSheet as any).orderMetadata,
        orderingStrategy: 'Global categories reversed (Technical before Performance), genre-specific categories respect user drag-and-drop order'
      });
    }
    
    return { categories: flattened, groupInfo };
  }

  // Fallback to empty object
  return { categories: {}, groupInfo: {} };
};


// Memoized criteria item component for better performance
const CriteriaItem = memo<CriteriaItemProps>(({ 
  categoryName, 
  index, 
  criteriaText, 
  feedback, 
  isAnimating, 
  onFeedbackChange, 
  universalConfig 
}) => {
  const handleNeedsWorkClick = useCallback(() => {
    onFeedbackChange(categoryName, index, criteriaText, 'needs-work');
  }, [categoryName, index, criteriaText, onFeedbackChange]);

  const handleGoodClick = useCallback(() => {
    onFeedbackChange(categoryName, index, criteriaText, 'good');
  }, [categoryName, index, criteriaText, onFeedbackChange]);

  return (
    <motion.div
      className={`relative p-2 rounded-lg border transition-all duration-200 cursor-pointer overflow-hidden flex items-center ${
        feedback === 'good' 
          ? 'bg-green-500/30 border-green-400/50 shadow-lg shadow-green-500/30' 
          : feedback === 'needs-work'
          ? 'bg-orange-500/30 border-orange-400/50 shadow-lg shadow-orange-500/30'
          : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
      }`}
      style={{ 
        minHeight: '36px', // Ensure adequate click area
        height: 'auto'
      }}
      animate={isAnimating ? { scale: [1, 1.02, 1] } : {}}
      transition={{ duration: 0.3 }}
    >
      {/* Left Half - Needs Work */}
      <div 
        className="absolute left-0 top-0 w-1/2 h-full z-10 hover:bg-orange-500/10 transition-all duration-200"
        onClick={handleNeedsWorkClick}
        title="Click to mark as 'Needs Work'"
      />
      
      {/* Right Half - Good */}
      <div 
        className="absolute right-0 top-0 w-1/2 h-full z-10 hover:bg-green-500/10 transition-all duration-200"
        onClick={handleGoodClick}
        title="Click to mark as 'Good'"
      />
      
      {/* Content */}
      <div className="relative z-0 w-full min-w-0">
        <div className={`${
          feedback === 'good' 
            ? 'text-green-100 font-medium' 
            : feedback === 'needs-work'
            ? 'text-orange-100 font-medium'
            : 'text-white/90'
        } ${universalConfig.captionText}`} title={criteriaText}>
          {criteriaText}
        </div>
      </div>
    </motion.div>
  );
});

CriteriaItem.displayName = 'CriteriaItem';

// Helper function to get group-specific styling
const getGroupStyling = (groupIndex: number) => {
  const styles = [
    // Group 0: Purple/Violet theme
    {
      headerBg: 'bg-purple-500/20',
      headerBorder: 'border-purple-400/40',
      headerText: 'text-purple-200',
      contentBg: 'bg-purple-500/5'
    },
    // Group 1: Blue theme  
    {
      headerBg: 'bg-blue-500/20',
      headerBorder: 'border-blue-400/40', 
      headerText: 'text-blue-200',
      contentBg: 'bg-blue-500/5'
    },
    // Group 2: Green theme
    {
      headerBg: 'bg-green-500/20',
      headerBorder: 'border-green-400/40',
      headerText: 'text-green-200', 
      contentBg: 'bg-green-500/5'
    },
    // Group 3: Orange theme
    {
      headerBg: 'bg-orange-500/20',
      headerBorder: 'border-orange-400/40',
      headerText: 'text-orange-200',
      contentBg: 'bg-orange-500/5'
    },
    // Group 4: Pink theme
    {
      headerBg: 'bg-pink-500/20',
      headerBorder: 'border-pink-400/40',
      headerText: 'text-pink-200',
      contentBg: 'bg-pink-500/5'
    }
  ];
  
  // Cycle through styles if we have more groups than styles
  return styles[groupIndex % styles.length];
};

// Memoized category column component
const CategoryColumn = memo<CategoryColumnProps>(({ 
  categoryName, 
  criteria, 
  feedback, 
  animatingItems, 
  groupInfo,
  onFeedbackChange, 
  universalConfig 
}) => {
  // Get group-specific styling
  const groupStyling = groupInfo ? getGroupStyling(groupInfo.groupIndex) : {
    headerBg: 'bg-white/10',
    headerBorder: 'border-white/10',
    headerText: 'text-white',
    contentBg: 'bg-white/5'
  };

  // Memoize the criteria items to prevent unnecessary re-renders
  const criteriaItems = useMemo(() => 
    criteria.map((criteriaText, index) => (
      <CriteriaItem
        key={`${categoryName}-${index}`}
        categoryName={categoryName}
        index={index}
        criteriaText={criteriaText}
        feedback={feedback[criteriaText]}
        isAnimating={animatingItems.has(criteriaText)}
        onFeedbackChange={onFeedbackChange}
        universalConfig={universalConfig}
      />
    )), 
    [categoryName, criteria, feedback, animatingItems, onFeedbackChange, universalConfig]
  );

  return (
    <div className="flex flex-col">
      {/* Category Header with group-specific styling */}
      <div className={`backdrop-blur-xl border rounded-lg px-2 py-2 mb-2 flex-shrink-0 ${groupStyling.headerBg} ${groupStyling.headerBorder}`}>
        <div className="flex items-center gap-1 min-w-0">
          <div className={`font-bold truncate ${groupStyling.headerText} ${universalConfig.smallText}`} title={categoryName}>
            {categoryName}
          </div>
        </div>
      </div>

      {/* Category Criteria - Vertical List */}
      <div className="space-y-1 px-1">
        {criteriaItems}
      </div>
    </div>
  );
});

CategoryColumn.displayName = 'CategoryColumn';

const StyleSpecificScoringCard: React.FC<StyleSpecificScoringCardProps> = ({
  currentRoutine = null,
  styleScoreSheets = [],
  styleCategories = [],
  onFeedbackUpdate = null,
  initialFeedback = {},
  className = "",
  containerSize: propContainerSize = 'medium'
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  // Use universal text sizing for consistency across all cards
  const { textSize } = useUniversalTextSizing();
  const universalConfig = getUniversalTextConfig(textSize);
  
  const [feedback, setFeedback] = useState<FeedbackData>(() => convertDatabaseFeedbackToInternal(initialFeedback));
  const [animatingItems, setAnimatingItems] = useState<Set<string>>(new Set());
  const isInternalUpdate = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Container-responsive design using the same pattern as PerformanceSchedule
  const { containerSize, dimensions } = useContainerSize(containerRef);
  
  // Debug: Track container size and dimensions changes
  useEffect(() => {
    console.log('🔍 [StyleSpecificScoringCard] useContainerSize changed:', {
      containerSize,
      dimensions,
      timestamp: new Date().toISOString()
    });
  }, [containerSize, dimensions.width, dimensions.height]);

  // Debounce the effectiveContainerSize to prevent rapid oscillation
  const [stableContainerSize, setStableContainerSize] = useState<ContainerSize>(containerSize || propContainerSize);
  const containerSizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const newSize = containerSize || propContainerSize;
    
    // Clear existing timeout
    if (containerSizeTimeoutRef.current) {
      clearTimeout(containerSizeTimeoutRef.current);
    }
    
    // Only update if actually different
    if (newSize !== stableContainerSize) {
      // Set timeout to prevent rapid oscillation
      containerSizeTimeoutRef.current = setTimeout(() => {
        console.log('📐 [StyleSpecificScoringCard] Stable container size updated:', {
          from: stableContainerSize,
          to: newSize,
          timestamp: new Date().toISOString()
        });
        setStableContainerSize(newSize);
      }, 300); // 300ms debounce for container size changes
    }
    
    return () => {
      if (containerSizeTimeoutRef.current) {
        clearTimeout(containerSizeTimeoutRef.current);
      }
    };
  }, [containerSize, propContainerSize, stableContainerSize]);

  // Add debounced dimensions with longer delay to prevent rapid updates
  const [stableDimensions, setStableDimensions] = useState(dimensions);
  const dimensionUpdateRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    console.log('⏱️ [StyleSpecificScoringCard] Dimensions changed, setting debounce timer:', {
      newDimensions: dimensions,
      currentStableDimensions: stableDimensions,
      timestamp: new Date().toISOString()
    });
    
    // Clear existing timeout
    if (dimensionUpdateRef.current) {
      clearTimeout(dimensionUpdateRef.current);
      console.log('🔄 [StyleSpecificScoringCard] Cleared existing dimension timeout');
    }
    
    // Set new timeout with longer delay to prevent rapid updates
    dimensionUpdateRef.current = setTimeout(() => {
      console.log('✅ [StyleSpecificScoringCard] Updating stable dimensions after 200ms delay:', {
        from: stableDimensions,
        to: dimensions,
        timestamp: new Date().toISOString()
      });
      setStableDimensions(dimensions);
    }, 200); // Increased to 200ms
    
    return () => {
      if (dimensionUpdateRef.current) {
        clearTimeout(dimensionUpdateRef.current);
      }
    };
  }, [dimensions.width, dimensions.height]);

  // Use stableContainerSize instead of effectiveContainerSize everywhere
  const effectiveContainerSize: ContainerSize = stableContainerSize;
  
  // Get the appropriate style score sheet for the current routine
  const currentStyleSheet = useMemo((): StyleScoreSheet | null => {
    if (!currentRoutine || !styleScoreSheets || styleScoreSheets.length === 0) {
      return null;
    }
    
    // Try to match by style category - check various possible field names
    const routineStyle = currentRoutine.category ||           // Check category field first (contains "Jazz")
                        currentRoutine.style || 
                        currentRoutine.styleCategory || 
                        currentRoutine['Dance Style'] ||
                        currentRoutine['Style'] ||
                        currentRoutine.division ||            // Check division later (contains "Solo")
                        currentRoutine.Category ||
                        currentRoutine.Type ||
                        currentRoutine.type;
    
    if (process.env.NODE_ENV === 'development') {
      // console.log('StyleSpecificScoringCard: Matching routine style:', {
      //   routineStyle,
      //   routineFields: {
      //     division: currentRoutine.division,
      //     style: currentRoutine.style,
      //     category: currentRoutine.category,
      //     styleCategory: currentRoutine.styleCategory,
      //     Category: currentRoutine.Category,
      //     Type: currentRoutine.Type,
      //     type: currentRoutine.type,
      //     'Dance Style': currentRoutine['Dance Style'],
      //     'Style': currentRoutine['Style']
      //   },
      //   allRoutineFields: Object.keys(currentRoutine),
      //   fullRoutine: currentRoutine, // This will show us all fields
      //   availableSheets: styleScoreSheets.map(sheet => ({
      //     styleId: sheet.styleId,
      //     styleName: sheet.styleName || 'Unnamed',
      //     hasNewFormat: !!sheet.styleSpecificData,
      //     hasOldFormat: !!sheet.categories,
      //     flattenedCategories: Object.keys(flattenStyleScoreSheet(sheet).categories)
      //   }))
      // });
    }
    
    if (routineStyle) {
      // Find matching style score sheet - prioritize exact matches first
      const styleKey = routineStyle.toLowerCase().trim();
      
      // First try to find an exact match
      let matchingSheet = styleScoreSheets.find(sheet => {
        if (!sheet.styleId) return false;
        const sheetStyleKey = sheet.styleId.toLowerCase().trim();
        return sheetStyleKey === styleKey;
      });
      
      // If no exact match, try partial matches but prioritize longer matches
      if (!matchingSheet) {
        // Sort by styleId length (descending) to prioritize longer, more specific matches
        const sortedSheets = [...styleScoreSheets].sort((a, b) => 
          (b.styleId?.length || 0) - (a.styleId?.length || 0)
        );
        
        matchingSheet = sortedSheets.find(sheet => {
          if (!sheet.styleId) return false;
          const sheetStyleKey = sheet.styleId.toLowerCase().trim();
          
          // Check for partial matches
          return styleKey.includes(sheetStyleKey) || 
                 sheetStyleKey.includes(styleKey);
        });
      }
      
      if (matchingSheet) {
        if (process.env.NODE_ENV === 'development') {
          // console.log('StyleSpecificScoringCard: Found matching sheet:', {
          //   styleId: matchingSheet.styleId,
          //   styleName: matchingSheet.styleName,
          //   hasNewFormat: !!matchingSheet.styleSpecificData,
          //   hasOldFormat: !!matchingSheet.categories,
          //   flattenedCategories: Object.keys(flattenStyleScoreSheet(matchingSheet).categories)
          // });
        }
        return matchingSheet;
      }
    }
    
    // If no specific match found, return null to show empty state
    if (process.env.NODE_ENV === 'development') {
      // console.log('StyleSpecificScoringCard: No matching sheet found for routine style:', routineStyle);
    }
    
    return null;
  }, [currentRoutine, styleScoreSheets]);

  // Container-responsive sizing using the same pattern as PerformanceSchedule
  const sizeConfig = useMemo((): SizeConfig => {
    const configs: Record<ContainerSize, SizeConfig> = {
      small: {
        headerSize: universalConfig.bodyText,
        categoryHeaderSize: universalConfig.smallText,
        criteriaSize: universalConfig.captionText,
        buttonSize: universalConfig.sectionPadding,
        iconSize: 12,
        spacing: 'space-y-1',
        categorySpacing: 'space-y-0.5',
        padding: universalConfig.sectionPadding,
        // Container-specific layout properties
        headerSpacing: 'mb-0.5',
        titleSize: universalConfig.smallText,
        iconSizeClass: 'w-3 h-3',
        buttonPadding: 'px-2 py-0.5',
        buttonTextSize: universalConfig.captionText,
        columnGap: 'gap-2',
        itemSpacing: 'space-y-0.5',
        baseOffset: 50,
        maxHeight: '90vh',
        minHeight: '200px'
      },
      medium: {
        headerSize: universalConfig.bodyText,
        categoryHeaderSize: universalConfig.smallText,
        criteriaSize: universalConfig.smallText,
        buttonSize: universalConfig.sectionPadding,
        iconSize: 16,
        spacing: 'space-y-2',
        categorySpacing: 'space-y-1',
        padding: universalConfig.sectionPadding,
        // Container-specific layout properties
        headerSpacing: 'mb-1',
        titleSize: universalConfig.bodyText,
        iconSizeClass: 'w-4 h-4',
        buttonPadding: 'px-2 py-1',
        buttonTextSize: universalConfig.smallText,
        columnGap: 'gap-3',
        itemSpacing: 'space-y-1',
        baseOffset: 60,
        maxHeight: '92vh',
        minHeight: '300px'
      },
      large: {
        headerSize: universalConfig.bodyText,
        categoryHeaderSize: universalConfig.bodyText,
        criteriaSize: universalConfig.bodyText,
        buttonSize: universalConfig.cardPadding,
        iconSize: 18,
        spacing: 'space-y-4',
        categorySpacing: 'space-y-2',
        padding: universalConfig.cardPadding,
        // Container-specific layout properties
        headerSpacing: 'mb-1',
        titleSize: universalConfig.cardTitle,
        iconSizeClass: 'w-5 h-5',
        buttonPadding: 'px-3 py-1',
        buttonTextSize: universalConfig.bodyText,
        columnGap: 'gap-4',
        itemSpacing: 'space-y-1.5',
        baseOffset: 70,
        maxHeight: '94vh',
        minHeight: '400px'
      },
      xlarge: {
        headerSize: universalConfig.bodyText,
        categoryHeaderSize: universalConfig.bodyText,
        criteriaSize: universalConfig.bodyText,
        buttonSize: universalConfig.cardPadding,
        iconSize: 20,
        spacing: 'space-y-6',
        categorySpacing: 'space-y-3',
        padding: universalConfig.cardPadding,
        // Container-specific layout properties
        headerSpacing: 'mb-1',
        titleSize: universalConfig.cardTitle,
        iconSizeClass: 'w-6 h-6',
        buttonPadding: 'px-3 py-1',
        buttonTextSize: universalConfig.bodyText,
        columnGap: 'gap-6',
        itemSpacing: 'space-y-2',
        baseOffset: 80,
        maxHeight: '96vh',
        minHeight: '500px'
      }
    };
    return configs[effectiveContainerSize];
  }, [effectiveContainerSize, universalConfig]);

  // Debounced animation cleanup to reduce frequent state updates
  const animationTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  
  const clearAnimationTimeout = useCallback((key: string) => {
    const timeoutId = animationTimeoutRef.current.get(key);
    if (timeoutId) {
      clearTimeout(timeoutId);
      animationTimeoutRef.current.delete(key);
    }
  }, []);

  // Handle feedback for a specific criteria - optimized version
  const handleCriteriaFeedback = useCallback((categoryName: string, criteriaIndex: number, criteriaText: string, feedbackType: 'good' | 'needs-work') => {
    const key = `${categoryName}:${criteriaText}`; // Use category and criteria text as the key to avoid conflicts
    
    // Clear any existing animation timeout for this key
    clearAnimationTimeout(key);
    
    // Add animation with optimized state update
    setAnimatingItems(prev => {
      if (prev.has(key)) return prev; // Avoid unnecessary updates
      return new Set(prev).add(key);
    });
    
    // Set timeout for animation cleanup
    const timeoutId = setTimeout(() => {
      setAnimatingItems(prev => {
        if (!prev.has(key)) return prev; // Avoid unnecessary updates
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
      animationTimeoutRef.current.delete(key);
    }, 300);
    
    animationTimeoutRef.current.set(key, timeoutId);

    // Batch state updates using React's automatic batching
    setFeedback(prev => {
      const currentValue = prev[key];
      let newFeedback: FeedbackData;
      
      // Toggle feedback: if same type is clicked, remove it; otherwise set new type
      if (currentValue === feedbackType) {
        const { [key]: removed, ...rest } = prev;
        newFeedback = rest;
      } else {
        newFeedback = { ...prev, [key]: feedbackType };
      }
      
      // Only update if there's actually a change
      if (JSON.stringify(newFeedback) === JSON.stringify(prev)) {
        return prev;
      }
      
      // Mark as internal update to prevent useEffect from running
      isInternalUpdate.current = true;
      
      // Call parent callback if provided - debounce this call
      if (onFeedbackUpdate) {
        // Use requestAnimationFrame to batch the callback
        requestAnimationFrame(() => {
          onFeedbackUpdate(newFeedback);
        });
      }
      
      return newFeedback;
    });
  }, [onFeedbackUpdate, clearAnimationTimeout]);

  // Clear all feedback
  const clearAllFeedback = useCallback(() => {
    // Mark as internal update to prevent useEffect from running
    isInternalUpdate.current = true;
    
    setFeedback({});
    if (onFeedbackUpdate) {
      onFeedbackUpdate({});
    }
  }, [onFeedbackUpdate]);

  // Update feedback when initialFeedback changes (but not during internal updates)
  useEffect(() => {
    // Skip update if this is an internal update to prevent infinite loops
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    
    // Convert database format to internal format if needed
    const convertedFeedback = convertDatabaseFeedbackToInternal(initialFeedback);
    
    // Only update if the feedback has actually changed
    setFeedback(prev => {
      if (JSON.stringify(prev) === JSON.stringify(convertedFeedback)) {
        return prev;
      }
      return convertedFeedback;
    });
  }, [initialFeedback]);

  // Container dimensions are now handled by useContainerSize hook
  
  // Cleanup animation timeouts and dimension timeout on unmount
  useEffect(() => {
    return () => {
      animationTimeoutRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      animationTimeoutRef.current.clear();
      if (dimensionUpdateRef.current) {
        clearTimeout(dimensionUpdateRef.current);
      }
      if (containerSizeTimeoutRef.current) {
        clearTimeout(containerSizeTimeoutRef.current);
      }
    };
  }, []);

  // Memoize feedback counts to prevent recalculation on every render
  const feedbackCounts = useMemo(() => {
    const totalFeedbackCount = Object.keys(feedback).length;
    const goodCount = Object.values(feedback).filter(f => f === 'good').length;
    const needsWorkCount = Object.values(feedback).filter(f => f === 'needs-work').length;
    
    return { totalFeedbackCount, goodCount, needsWorkCount };
  }, [feedback]);

  // Get the flattened categories and group info for the current style sheet
  const { flattenedCategories, groupInfo } = useMemo(() => {
    if (!currentStyleSheet) {
      return { flattenedCategories: {}, groupInfo: {} };
    }
    
    const result = flattenStyleScoreSheet(currentStyleSheet);
    
    return { flattenedCategories: result.categories, groupInfo: result.groupInfo };
  }, [currentStyleSheet]);

  // Calculate column distribution - DECOUPLED from live dimensions to prevent glitching
  const columnDistribution = useMemo(() => {
    const categories = Object.entries(flattenedCategories);
    if (categories.length === 0) {
      return [];
    }

    // Use STABLE WIDTH-BASED column count to prevent oscillation but maintain responsiveness
    const getColumnCount = () => {
      // Use stableDimensions.width directly instead of container size categories
      const width = stableDimensions.width;
      
      // Width-based breakpoints (more stable than container size categories)
      if (width >= 1200) return 6;      // Very wide
      if (width >= 1000) return 5;      // Wide  
      if (width >= 800) return 4;       // Medium-wide
      if (width >= 600) return 3;       // Medium
      if (width >= 400) return 2;       // Small
      return 1;                         // Very small
      
      // Fallback for zero/invalid width
      if (width === 0) return 4; // Default when dimensions not yet measured
    };

    const columnCount = getColumnCount();
    
    // Create individual items with category context
    type CriteriaItem = {
      categoryName: string;
      criteriaText: string;
      criteriaIndex: number;
      groupInfo?: { groupName: string, groupIndex: number };
      showCategoryHeader: boolean;
    };
    
    const allItems: CriteriaItem[] = [];
    categories.forEach(([categoryName, criteria]) => {
      criteria.forEach((criteriaText, index) => {
        allItems.push({
          categoryName,
          criteriaText,
          criteriaIndex: index,
          groupInfo: groupInfo[categoryName],
          showCategoryHeader: false
        });
      });
    });

    // Distribute items evenly across columns - NO dimension calculations
    const columns: Array<CriteriaItem[]> = Array.from({ length: columnCount }, () => []);
    const itemsPerColumn = Math.ceil(allItems.length / columnCount);
    
    let currentColumn = 0;
    const categorySeenInColumns = new Set<string>();
    
    allItems.forEach((item) => {
      if (columns[currentColumn].length >= itemsPerColumn && currentColumn < columnCount - 1) {
        currentColumn++;
      }
      
      if (!categorySeenInColumns.has(item.categoryName)) {
        item.showCategoryHeader = true;
        categorySeenInColumns.add(item.categoryName);
      }
      
      columns[currentColumn].push(item);
    });
    
    const filledColumns = columns.filter(column => column.length > 0);
    
    return filledColumns;
  }, [flattenedCategories, groupInfo, stableDimensions.width]); 
  // Using stableDimensions.width instead of effectiveContainerSize to prevent oscillation

  // Early return if no style sheet is available
  if (!currentStyleSheet || Object.keys(flattenedCategories).length === 0) {
    return (
      <div ref={containerRef} className={`${className} ${sizeConfig.padding} h-full flex flex-col items-center justify-center relative`}>
        <div className="text-center">
          <Target className="w-12 h-12 mx-auto mb-3 opacity-50 text-purple-400" />
          <BodyText className="font-medium text-white mb-2">
            No Style-Specific Criteria Available
          </BodyText>
          <SmallText className="text-white/60">
            Configure style score sheets in the admin settings to enable style-specific feedback.
          </SmallText>
        </div>
      </div>
    );
  }

  const { totalFeedbackCount, goodCount, needsWorkCount } = feedbackCounts;

  return (
    <div ref={containerRef} className={`${className} h-full flex flex-col min-h-0 relative`}>
      {/* Header */}
      <div className={`flex justify-between items-center ${sizeConfig.headerSpacing} flex-shrink-0 px-1`}>
        <CardTitle className={`flex items-center ${universalConfig.spacing} ${sizeConfig.titleSize}`}>
          <Sparkles className={`${sizeConfig.iconSizeClass} text-purple-400`} />
          {currentStyleSheet?.styleName || 'STYLE-SPECIFIC'} FEEDBACK
        </CardTitle>
        
        <div className="flex items-center gap-2">
          <UniversalButton
            onClick={clearAllFeedback}
            variant="secondary"
            size="small"
            className={`flex items-center gap-1 ${sizeConfig.buttonPadding}`}
            disabled={totalFeedbackCount === 0}
          >
            <RotateCcw className={sizeConfig.iconSizeClass} />
            <span className={sizeConfig.buttonTextSize}>Clear All</span>
          </UniversalButton>
        </div>
      </div>

      {/* Feedback Summary - Always Visible */}
      <div className={`flex gap-2 ${sizeConfig.headerSpacing} flex-shrink-0 px-1`}>
        <span className={`${sizeConfig.buttonTextSize} ${sizeConfig.buttonPadding} bg-green-500/20 text-green-300 rounded-full border border-green-400/30`}>
          ✓ {goodCount} Good
        </span>
        <span className={`${sizeConfig.buttonTextSize} ${sizeConfig.buttonPadding} bg-orange-500/20 text-orange-300 rounded-full border border-orange-400/30`}>
          ⚠ {needsWorkCount} Needs Work
        </span>
      </div>

      {/* Category Columns Layout - Container-Responsive Column Distribution */}
      <div className="flex-1 min-h-0 relative flex flex-col">
        {/* Nested Card Container - Same pattern as RoutineMetadataCard */}
        <div className="bg-white/5 border border-white/10 rounded-lg flex-1 min-h-0 flex flex-col">
          {/* Content Area with container-responsive padding */}
          <div className={`flex-1 overflow-y-auto ${effectiveContainerSize === 'small' ? 'pb-12' : 'pb-16'}`}>
            {/* Container-responsive column layout for individual criteria items */}
            <div className={`flex ${sizeConfig.columnGap} ${sizeConfig.padding}`}>
              {columnDistribution.map((columnItems, columnIndex) => {
                // Use FIXED gap instead of dynamic calculation to prevent glitching
                const fixedGap = 4; // 4px gap for consistent spacing
                
                return (
                  <div 
                    key={columnIndex} 
                    className="flex-1 flex flex-col min-w-0"
                    style={{ minHeight: '100%' }}
                  >
                    <div 
                      className={`flex-1 flex flex-col justify-start min-h-full ${sizeConfig.itemSpacing}`}
                    >
                    {columnItems.map((item, itemIndex) => {
                      const groupStyling = item.groupInfo ? getGroupStyling(item.groupInfo.groupIndex) : {
                        headerBg: 'bg-white/10',
                        headerBorder: 'border-white/10',
                        headerText: 'text-white',
                        contentBg: 'bg-white/5'
                      };

                      return (
                        <div key={`${item.categoryName}-${item.criteriaIndex}`}>
                          {/* Category Header - only show if showCategoryHeader is true */}
                          {item.showCategoryHeader && (
                            <div className={`backdrop-blur-xl border rounded-lg ${sizeConfig.buttonPadding} ${sizeConfig.headerSpacing} ${groupStyling.headerBg} ${groupStyling.headerBorder}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <div className={`font-bold truncate ${groupStyling.headerText} ${sizeConfig.categoryHeaderSize}`} title={item.categoryName}>
                                  {item.categoryName}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Individual Criteria Item */}
                          <div className="px-1">
                            <CriteriaItem
                              categoryName={item.categoryName}
                              index={item.criteriaIndex}
                              criteriaText={item.criteriaText}
                              feedback={feedback[`${item.categoryName}:${item.criteriaText}`]}
                              isAnimating={animatingItems.has(`${item.categoryName}:${item.criteriaText}`)}
                              onFeedbackChange={handleCriteriaFeedback}
                              universalConfig={universalConfig}
                            />
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Fixed Visual Bottom Divider - Absolutely positioned within nested card */}
          <div className="absolute bottom-0 left-0 right-0 px-2 py-4 bg-gradient-to-t from-black/80 via-black/50 to-transparent pointer-events-none">
            <div className="w-full h-1 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full shadow-lg"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Memoize the main component to prevent unnecessary re-renders
const MemoizedStyleSpecificScoringCard = memo(StyleSpecificScoringCard);
MemoizedStyleSpecificScoringCard.displayName = 'StyleSpecificScoringCard';

export default MemoizedStyleSpecificScoringCard;
